/** 退款与取消政策正文（不含页面级包装，便于 /refund 与 /legal 复用） */
export default function RefundPolicyContent() {
  return (
    <>
      <h1 className="text-3xl font-bold mb-2">
        Refund and Cancellation Policy
      </h1>
      <p className="text-gray-500 mb-8">
        Effective Date: January 1, 2026 &nbsp;|&nbsp; Last Updated: September 29, 2026
      </p>

      <div className="space-y-8 text-gray-700 leading-relaxed">
        <section>
          <h2 className="text-xl font-semibold mb-3">1. Overview</h2>
          <p>
            BamaBang provides digital matchmaking and subscription services. Due
            to the nature of digital products, our refund policy is designed to
            be fair and transparent while complying with applicable consumer
            protection laws.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">
            2. Digital Products & Subscriptions
          </h2>
          <p className="mb-3">
            All sales of digital products — including 30-day challenge fees, VIP day
            passes and premium memberships — are <strong>final</strong> once activated or
            consumed. We do not offer general refunds for these items. VIP day passes are
            consumable: one day is deducted from your balance every calendar day while the
            balance is positive, whether or not you use the app.
          </p>
          <p>
            VIP memberships may be offered as <strong>auto-renewable subscriptions</strong> via the Apple App Store or Google Play. These subscriptions automatically renew unless canceled at least 24 hours before the end of the current billing cycle.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">
            3. Cancellation Policy
          </h2>
          <p className="mb-3">
            You may cancel your active subscription or challenge participation
            at any time. Upon cancellation:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Your premium features and challenge progress remain active until the end of
              your current billing cycle, challenge period, or remaining pass balance.
            </li>
            <li>No further charges will be made after cancellation.</li>
            <li>
              Cancellation does not entitle you to a prorated refund for the
              remaining unused portion of the current period.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">
            4. Exceptions & Special Circumstances
          </h2>
          <p className="mb-3">
            We may consider refunds on a case-by-case basis under the following
            circumstances:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Technical Issues:</strong> If you experience severe
              technical problems that completely prevent you from accessing or
              using the paid service, and our support team is unable to resolve
              the issue within a reasonable timeframe.
            </li>
            <li>
              <strong>Duplicate Charges:</strong> If you are charged multiple
              times for the same purchase due to a payment processing error.
            </li>
            <li>
              <strong>Unauthorized Transactions:</strong> If a charge was made
              without your authorization.
            </li>
          </ul>
          <p className="mt-3">
            To request a refund under these exceptions, please contact our
            support team at{" "}
            <a
              href="mailto:bamabang.info@gmail.com"
              className="text-pink-600 underline font-medium"
            >
              bamabang.info@gmail.com
            </a>{" "}
            within <strong>7 days</strong> of the purchase date.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">
            5. Store Purchases (Apple and Google)
          </h2>
          <p className="mb-3">
            All purchases are made through the app stores, so refunds are issued by the store,
            not by us. We cannot issue refunds for store transactions directly.
          </p>
          <p>
            <strong>Apple App Store:</strong> purchases are subject to Apple's refund policies.
            To request a refund, please visit{" "}
            <a
              href="https://reportaproblem.apple.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pink-600 underline"
            >
              reportaproblem.apple.com
            </a>{" "}
            or contact Apple Support directly.
          </p>
          <p className="mt-3">
            <strong>Google Play:</strong> purchases are subject to Google Play's refund policies.
            You can request a refund from{" "}
            <a
              href="https://play.google.com/store/account/orderhistory"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pink-600 underline"
            >
              your Google Play order history
            </a>{" "}
            or through Google Play support.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">
            6. How to Cancel Your Subscription
          </h2>
          <p className="mb-3">
            To cancel an auto-renewable subscription, you must do so directly through the platform where you made the purchase. Uninstalling the app or deleting your account will not automatically cancel your subscription.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>For iOS (Apple App Store):</strong> Go to your iPhone Settings &gt; Apple ID &gt; Subscriptions to manage and cancel.
            </li>
            <li>
              <strong>For Android (Google Play):</strong> Go to the Google Play app &gt; Profile icon &gt; Payments &amp; subscriptions.
            </li>
          </ul>
          <p className="mt-3">
            Deleting your BamaBang account does <strong>not</strong> cancel an active subscription.
            Please cancel it in the store account settings above before or after deleting your
            account.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">
            7. Crowdfunding Supporter Codes
          </h2>
          <p className="mb-3">
            Supporter codes are rewards of our crowdfunding campaigns. Payment for a campaign
            is handled by the crowdfunding platform under its own terms; supporters cannot cancel
            a pledge after it is made.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>A code must be redeemed by the deadline stated when it was issued (within six months of issue). Expired codes cannot be redeemed and are not refunded.</li>
            <li>Once redeemed, VIP days are tied to the account, cannot be transferred, and are non-refundable.</li>
            <li>If you lost a code, contact us with your crowdfunding backer number and we will reissue it if it has not been redeemed; the deadline is not extended.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3">8. Contact Us</h2>
          <p>
            If you have any questions about this Refund and Cancellation Policy,
            please contact us at{" "}
            <a
              href="mailto:bamabang.info@gmail.com"
              className="text-pink-600 underline font-medium"
            >
              bamabang.info@gmail.com
            </a>
            .
          </p>
        </section>
      </div>
    </>
  );
}
